import React from 'react';
import { X, Scan, Plus, Minus, Sun, Moon } from 'lucide-react';

const CANVAS_W = 1320;
const CANVAS_H = 780;

/**
 * Shared pan/zoom/theme chrome for LineageModal and WorkflowModal.
 * Pass header + canvas body via props; shell owns interaction state.
 */
export default class GraphCanvasShell extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            zoom: 1.0,
            marginLeft: 24,
            marginTop: 24,
            ready: false,
            theme: props.defaultTheme || 'light'
        };
        this.stageRef = React.createRef();
        this.panActive = false;
        this.panStartX = 0;
        this.panStartY = 0;
        this.panStartScrollLeft = 0;
        this.panStartScrollTop = 0;
        this.resizeObserver = null;
    }

    componentDidMount() {
        if (this.props.show) {
            this.openShell();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.show !== this.props.show) {
            if (this.props.show) {
                this.openShell();
            } else {
                this.closeShell();
            }
            return;
        }

        // Re-center when graph content finishes loading / changes
        if (
            this.props.show
            && (prevProps.children !== this.props.children || prevProps.contentKey !== this.props.contentKey)
        ) {
            requestAnimationFrame(() => {
                this.updateLayout({ centerScroll: true });
                if (!this.state.ready) this.setState({ ready: true });
            });
        }
    }

    componentWillUnmount() {
        this.closeShell();
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    }

    openShell = () => {
        document.body.style.overflow = 'hidden';
        this.observeStage();
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.updateLayout({ centerScroll: true });
                this.setState({ ready: true });
            });
        });
    };

    closeShell = () => {
        document.body.style.overflow = '';
        this.unobserveStage();
        this.setState({ ready: false });
    };

    observeStage = () => {
        this.unobserveStage();
        const stage = this.stageRef.current;
        if (!stage || typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver(() => {
            this.updateLayout({ centerScroll: false });
        });
        this.resizeObserver.observe(stage);
    };

    unobserveStage = () => {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    };

    updateLayout = ({ centerScroll = false } = {}) => {
        const stage = this.stageRef.current;
        if (!stage) return;

        const { zoom } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const visualW = CANVAS_W * validZoom;
        const visualH = CANVAS_H * validZoom;

        let marginLeft = 24;
        let marginTop = 24;

        if (stage.clientWidth > 0) {
            marginLeft = Math.max(24, (stage.clientWidth - visualW) / 2);
        }
        if (stage.clientHeight > 0) {
            marginTop = Math.max(24, (stage.clientHeight - visualH) / 2);
        }

        const marginChanged =
            Math.abs(marginLeft - this.state.marginLeft) > 0.5
            || Math.abs(marginTop - this.state.marginTop) > 0.5;

        const applyScroll = () => {
            if (!centerScroll || !this.stageRef.current) return;
            const el = this.stageRef.current;
            // Center overflow so the graph isn't stuck at the bottom/top
            el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
            el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
        };

        if (marginChanged) {
            this.setState({ marginLeft, marginTop }, applyScroll);
        } else {
            applyScroll();
        }
    };

    handleMouseDown = (e) => {
        if (e.target.closest('.lin-zoom') || e.target.closest('.lin-legend')) return;
        this.panActive = true;
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
        this.panStartScrollLeft = this.stageRef.current ? this.stageRef.current.scrollLeft : 0;
        this.panStartScrollTop = this.stageRef.current ? this.stageRef.current.scrollTop : 0;
        if (this.stageRef.current) this.stageRef.current.classList.add('grabbing');
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e) => {
        if (!this.panActive || !this.stageRef.current) return;
        this.stageRef.current.scrollLeft = this.panStartScrollLeft - (e.clientX - this.panStartX);
        this.stageRef.current.scrollTop = this.panStartScrollTop - (e.clientY - this.panStartY);
    };

    handleMouseUp = () => {
        this.panActive = false;
        if (this.stageRef.current) this.stageRef.current.classList.remove('grabbing');
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    handleZoom = (dir) => {
        this.setState((prev) => {
            let nextZoom = prev.zoom;
            const stage = this.stageRef.current;
            if (dir === 'fit') {
                if (stage && stage.clientWidth > 0 && stage.clientHeight > 0) {
                    const fitW = (stage.clientWidth - 96) / CANVAS_W;
                    const fitH = (stage.clientHeight - 72) / CANVAS_H;
                    nextZoom = Math.min(1, Math.max(0.5, Math.min(fitW, fitH)));
                } else {
                    nextZoom = 1.0;
                }
            } else {
                const step = typeof dir === 'number' ? dir : 0;
                nextZoom = Math.min(1.6, Math.max(0.5, prev.zoom + step * 0.1));
            }

            if (isNaN(nextZoom) || nextZoom <= 0) {
                nextZoom = 1.0;
            }
            return { zoom: nextZoom };
        }, () => {
            this.updateLayout({ centerScroll: true });
        });
    };

    render() {
        if (!this.props.show) return null;

        const { zoom, marginLeft, marginTop, theme, ready } = this.state;
        const {
            onClose,
            title,
            stats,
            headerExtra = null,
            legend,
            children
        } = this.props;

        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        // Compensate layout size so transform:scale doesn't leave empty scroll space
        const layoutShrinkX = CANVAS_W * (validZoom - 1);
        const layoutShrinkY = CANVAS_H * (validZoom - 1);

        return (
            <div className="lin-overlay on" id="linOverlay">
                <div className={`lin-modal ${theme === 'dark' ? 'dark-theme' : ''}`}>
                    <div className="lin-head">
                        <div>
                            <div className="lin-h-title">{title}</div>
                            {stats}
                        </div>
                        <div className="lin-h-r">
                            {headerExtra}
                            <span className="lin-hicon"></span>
                            <button
                                className="lin-close tw-mr-2"
                                onClick={() => this.setState({ theme: theme === 'dark' ? 'light' : 'dark' })}
                                title={theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
                            >
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                            <button className="lin-close" onClick={onClose}><X size={18} /></button>
                        </div>
                    </div>

                    <div className="lin-viewport">
                        <div
                            className="lin-stage"
                            id="linStage"
                            ref={this.stageRef}
                            onMouseDown={this.handleMouseDown}
                        >
                            <div
                                className="lin-canvas"
                                id="linCanvas"
                                style={{
                                    transform: `scale(${validZoom})`,
                                    width: `${CANVAS_W}px`,
                                    height: `${CANVAS_H}px`,
                                    marginLeft: `${marginLeft}px`,
                                    marginTop: `${marginTop}px`,
                                    marginRight: `${24 + layoutShrinkX}px`,
                                    marginBottom: `${24 + layoutShrinkY}px`,
                                    opacity: ready ? 1 : 0,
                                    transition: 'opacity 0.15s ease'
                                }}
                            >
                                {children}
                            </div>
                        </div>

                        <div className="lin-zoom">
                            <button onClick={() => this.handleZoom('fit')} title="Fit to screen"><Scan size={17} /></button>
                            <button onClick={() => this.handleZoom(1)}><Plus size={17} /></button>
                            <span className="pct">{Math.round(validZoom * 100)}%</span>
                            <button onClick={() => this.handleZoom(-1)}><Minus size={17} /></button>
                        </div>

                        {legend}
                    </div>
                </div>
            </div>
        );
    }
}
